import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";
import { addEditBrand, brandDataProps, getAllProductsBasedOnBrandsPayload, getAllProductsBasedOnBrandsResponse, GetOnePosBrand, responseProps, selectedProductsProps, setBrandDetailsProps, setBrandProps } from "../../components/brand/brandModule";
import { ZenToast } from "zen-core-elements";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query";
import { CategoryProductPayload } from "./categorySlice";
import { CategoryProductItem, CategoryProductRes } from "../../components/category/categoryModule";

interface initialState {
    brandList: { value: setBrandProps[], error: string | null, count: number | null, loader: boolean },
    onePosBrand: { value: GetOnePosBrand | null, error: string | null, loading: boolean },
    createPosBrand: { loading: boolean }
    updatePosBrand: { loading: boolean }
    allBrandProducts: { value: CategoryProductItem[], count: number }
    oneBrandProducts: { value: CategoryProductItem[], id: number, count: number }
}
const initialState: initialState = {
    brandList: { count: null, error: null, value: [], loader: false },
    onePosBrand: { value: null, error: null, loading: false },
    createPosBrand: { loading: false },
    updatePosBrand: { loading: false },
    oneBrandProducts: { value: [], count: 0, id: -1 },
    allBrandProducts: { value: [], count: 0 }
}

export const getAllBrands = createAsyncThunk("brands/getAllPosBrands", ({ storeId, data }: { storeId: number, data: brandDataProps }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPosBrands, { storeId: storeId })
    return HttpRoutingService?.getMethod<responseProps>(url, data)
})
export const deletePosBrand = createAsyncThunk("brands/deleteBrand", ({ storeId, id }: { storeId: number, id: number | number[] }) => {
    const url = HttpRoutingService?.replaceUrl(API?.deleteBrand, { storeId: storeId })
    return typeof id == "number" ?
        HttpRoutingService?.deleteMethod(url + id) :
        HttpRoutingService?.putMethod(url + "delete", { id })
})
export const onePosBrand = createAsyncThunk("brands/getOnePosBrand", ({ storeId, id }: { storeId: number, id: number | undefined }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getOnePosBrand, { storeId: storeId, id: id })
    return HttpRoutingService?.getMethod<{ brand: GetOnePosBrand }>(url)
})
export const getAllProductsBasedOnBrands = createAsyncThunk("brands/getAllProductsBasedOnBrands", ({ storeId: storeId, data, isAllProduct }: CategoryProductPayload) => {
    const url = HttpRoutingService.replaceUrl(API.getAllPosCategoryProducts, { storeId: storeId })
    return HttpRoutingService.getMethod<CategoryProductRes>(url, data)
})

export const deleteBrandProducts = createAsyncThunk("brands/deleteBrandProducts", ({ id, payload, storeId }: { id: number, storeId: number, payload: { products: number[] } }) => {
    const url = HttpRoutingService.replaceUrl(API.deleteBrandProducts, { storeId: storeId, id: id })
    return HttpRoutingService.putMethod(url, payload);
})
export const createPosBrand = createAsyncThunk("brands/createBrand", ({ storeId, data }: { storeId: number, data: addEditBrand }) => {
    const url = HttpRoutingService?.replaceUrl(API?.createBrand, { storeId: storeId })
    return HttpRoutingService?.postMethod<setBrandDetailsProps>(url, data)
})
export const updatePosBrand = createAsyncThunk("brands/updateBrand", ({ storeId, id, data }: { storeId: number, id: number | undefined, data: addEditBrand }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getBrand, { storeId: storeId, id: id })
    return HttpRoutingService?.putMethod(url, data)

})

export const brandSlice = createSlice({
    name: "brands",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getAllBrands?.pending, (state, action) => {
                state.brandList.loader = true
            })
            .addCase(getAllBrands?.fulfilled, (state, action) => {
                let { data: { offset } } = action?.meta?.arg
                if (offset > 0) {
                    if (action.payload?.brands?.rows)
                        state.brandList.value = [...state.brandList.value, ...action.payload?.brands?.rows]
                }
                else {
                    state.brandList.value = action.payload?.brands?.rows || []
                }
                state.brandList.count = action.payload?.brands?.count
                state.brandList.loader = false
            })
            .addCase(getAllBrands?.rejected, (state, action) => {
                state.brandList.error = action?.error?.message as string
                state.brandList.loader = false
            })
            .addCase(deletePosBrand?.pending, (state, action) => {
                state.brandList.loader = true
            })
            .addCase(deletePosBrand?.rejected, (state, action) => {
                state.brandList.loader = false
                ZenToast({
                    type: "error",
                    message: "Error while deleting a brand"
                })
            })
            .addCase(onePosBrand?.pending, (state) => {
                state.onePosBrand.loading = true
            })
            .addCase(onePosBrand?.fulfilled, (state, action) => {
                state.onePosBrand.value = action.payload?.brand
                state.onePosBrand.loading = false
            })
            .addCase(onePosBrand?.rejected, (state, action) => {
                state.onePosBrand.loading = false
                state.onePosBrand.error = action?.error?.message as string
            })

            .addCase(createPosBrand?.pending, (state) => {
                state.createPosBrand.loading = true
            })
            .addCase(createPosBrand?.fulfilled, (state, action) => {
                state.createPosBrand.loading = false
            })
            .addCase(createPosBrand?.rejected, (state, action) => {
                state.createPosBrand.loading = false
            })

            .addCase(updatePosBrand?.pending, (state) => {
                state.updatePosBrand.loading = true
            })
            .addCase(updatePosBrand?.fulfilled, (state, action) => {
                state.updatePosBrand.loading = false
            })
            .addCase(updatePosBrand?.rejected, (state, action) => {
                state.updatePosBrand.loading = false
            })

            .addCase(getAllProductsBasedOnBrands?.fulfilled, (state, action) => {
                const products = action?.payload?.products?.rows || []
                const { offset, brandId } = action?.meta?.arg?.data!
                //All products for brand
                if (brandId == undefined) {
                    const brandIds = new Set(state?.oneBrandProducts?.value?.map(p => p.id));
                    const result = products.filter(p => !brandIds.has(p.id));
                    state.allBrandProducts.count = Number(action?.payload?.products?.count)
                    if (offset > 0) {
                        state.allBrandProducts.value = [...state.allBrandProducts?.value, ...result]
                    }
                    else {
                        state.allBrandProducts.value = [...result]
                    }
                }
                //one brand products
                else {
                    state.oneBrandProducts.count = Number(action?.payload?.products?.count)
                    state.oneBrandProducts.id = brandId
                    if (offset > 0) {
                        state.oneBrandProducts.value = [...state.oneBrandProducts?.value, ...products]
                    }
                    else {
                        state.oneBrandProducts.value = [...products]
                    }
                }
            })
            .addCase(getAllProductsBasedOnBrands?.rejected, (state, action) => {
                ZenToast({
                    type: "error",
                    message: "Failed to get all products"
                })
            })
    }
})

export default brandSlice?.reducer